import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import dayjs from 'dayjs';

import { TextEditor } from '../TextEditor';
import { ColorPalette } from '../ColorPalette';
import { DateField } from '../DateField';
import { EventLayout } from '../EventLayout';
import { deleteCategoryAPI, getCategoriesAPI, updateCategoryAPI } from '../../../api/eventAttrsAPI';
import { addCategory, deleteCategory, fetchedCategories, updateCategory } from '../../../store/categories';
import SearchBar from '../SearchBar';
import OutsideClick from '../../../functions/OutsideClick';
import { FloatingWindow } from '../../utility/FloatingWindow';
import { DESCRIPTION_WINDOW_MIN, WINDOW_CANCEL, descriptionWindowSize } from '../../../functions/dayWindows';

import './styles.scss'
import { addEvent, getEventById, removeEvent, updateEvent, updateEventsPositions } from '../../../store/events';
import { loadEventMonths } from '../../../store/eventsLoader';
import { addEventAPI, deleteEventAPI, updateEventAPI } from '../../../api';
import { isFitTitle, unwrapFit, wrapFit } from '../../../functions/titleFit';
import { DEFAULT_TEXT_COLOR } from '../../../functions/richText';
import { LAYOUTS, NO_ROOM_MESSAGE, canPlace, eventsOnDay, firstFreePlace, isDayFullError, noRoomMessage, rearranged } from '../../../functions/dayLimit';

// The description's editor is a library of its own, loaded with the form rather than with the app.
const DescriptionEditor = lazy(() => import('../DescriptionEditor'));

// From this width, as on the whole screen, the form's settings go to a panel at its side, and the title and
// description take the rest.
const WIDE_FORM = 1000;

const DETACH_ICON = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 4h6v6M20 4l-8 8M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </svg>
);

const placeOf = (event) => ({ width: event.width, height: event.height, pos_x: event.pos_x, pos_y: event.pos_y });

function EventForm({ eventId, toggleForm, date: windowDate }) {
    const dispatch = useDispatch();

    const initialEvent = useSelector(state => getEventById(state, eventId));
    // The day the event is for, 'YYYY-MM-DD': the day window's at first, and any other the user picks.
    const [day, setDay] = useState(dayjs(initialEvent ? initialEvent.date : windowDate).format('YYYY-MM-DD'));
    const date = dayjs(day).set('hour', 12).set('minute', 0).set('second', 0);
    const dayEvents = useSelector(state => eventsOnDay(state, day), shallowEqual);
    const categories = useSelector(state => state.categories.categories)
    const colors = useSelector(state => state.colors.colors);
    // The title as the editor has it, and whether it's sized «Авто», which wraps it when saved.
    const [title, setTitle] = useState(initialEvent ? unwrapFit(initialEvent.title) : '');
    const [titleFit, setTitleFit] = useState(initialEvent ? isFitTitle(initialEvent.title) : false);
    const [description, setDescription] = useState(initialEvent ? initialEvent.description : '');
    const [color, setColor] = useState(initialEvent ? initialEvent.color : colors[1].color);
    const [rating, setRating] = useState(initialEvent ? initialEvent.rating : 0);
    const [icon, setIcon] = useState(initialEvent ? initialEvent.icon : '');
    const [fontSize,] = useState(initialEvent ? initialEvent.fontSize : "1.5rem");
    // The event's size and place in its day, as { width, height, pos_x, pos_y }.
    const [place, setPlace] = useState(initialEvent ? placeOf(initialEvent) : firstFreePlace(dayEvents, null));
    const [category, setCategory] = useState(
        initialEvent &&
            initialEvent.category &&
            categories.find(category => category.name === initialEvent.category) ?
            {
                id: categories.find(category => category.name === initialEvent.category).id,
                name: initialEvent.category,
            } :
            { id: null, name: '', isEditable: false });

    // Delete asks first, in a box under its button, which a click elsewhere closes.
    const [confirmEventDeletion, setConfirmEventDeletion] = useState(false);
    const deleteRef = useRef(null);
    OutsideClick(deleteRef, () => setConfirmEventDeletion(false));
    // The format painter, shared by the title and the description: { format, sticky } or null.
    const [painter, setPainter] = useState(null);
    // Whether the description is in a window of its own, which opens beside the form's.
    const [descriptionApart, setDescriptionApart] = useState(false);
    const formRef = useRef(null);
    const [wide, setWide] = useState(false);
    // Measured at once as well, so that a form opening on the whole screen isn't drawn narrow first.
    useLayoutEffect(() => {
        const form = formRef.current;
        const measure = () => setWide(form.offsetWidth >= WIDE_FORM);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(form);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!painter) return undefined;
        const onKeyDown = (e) => { if (e.key === 'Escape') setPainter(null); };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [painter]);

    useEffect(() => {
        if (initialEvent) {
            setTitle(unwrapFit(initialEvent.title));
            setTitleFit(isFitTitle(initialEvent.title));
            setDescription(initialEvent.description);
            setColor(initialEvent.color);
            setRating(initialEvent.rating);
            setIcon(initialEvent.icon);
            setPlace(placeOf(initialEvent));
            let categoryData = categories.find(category => category.name === initialEvent.category);
            if (categoryData) {
                setCategory({ id: categoryData.id, name: categoryData.name, isEditable: true });
            } else {
                setCategory({ id: null, name: initialEvent.category, isEditable: false });
            }
        }
    }, [initialEvent])

    // A day outside the calendar's loaded months has its events fetched, to know where there's room.
    useEffect(() => {
        dispatch(loadEventMonths([dayjs(day).format('YYYY-MM')]));
    }, [day, dispatch]);

    // When the day changes, or its events come in, a place that's no longer free gives way to the first free one,
    // of the same size if there's one, or to none when the day is full.
    useEffect(() => {
        if (place && canPlace(dayEvents, initialEvent, place)) return;
        const sameSize = LAYOUTS.find(layout => layout.width === place?.width && layout.height === place?.height);
        const next = sameSize?.spots
            .map(([pos_x, pos_y]) => ({ width: sameSize.width, height: sameSize.height, pos_x, pos_y }))
            .find(spot => canPlace(dayEvents, initialEvent, spot));
        setPlace(next ?? firstFreePlace(dayEvents, initialEvent));
    }, [dayEvents])

    const savedTitle = wrapFit(title, titleFit);

    const createEvent = () => {
        let categoryData = category.id;
        if (category.id === null && category.name.length > 0) {
            categoryData = category.name;
        }

        addEventAPI({
            title: savedTitle,
            description,
            date,
            rating,
            icon,
            color,
            fontSize,
            ...place,
            category: category.name,
            order: dayEvents.length,
        }).then(res => {
            if (typeof categoryData === "string") {
                dispatch(addCategory({
                    id: res.data.category,
                    name: category.name
                }))
            }

            dispatch(addEvent({
                id: res.data.id,
                title: savedTitle,
                description,
                date,
                rating,
                icon,
                color,
                fontSize,
                width: place.width,
                height: place.height,
                pos_x: res.data.pos_x,
                pos_y: res.data.pos_y,
                category: res.data.category,
                order: res.data.order,
            }))
            if (rearranged(res).length) dispatch(updateEventsPositions(rearranged(res)));
        }).catch((err) => {
            if (isDayFullError(err)) alert(NO_ROOM_MESSAGE);
            else console.log(err);
        })

        toggleForm(false);
    };

    const editEvent = () => {
        updateEventAPI({
            id: eventId,
            title: savedTitle,
            description,
            date,
            rating,
            icon,
            color,
            fontSize,
            ...place,
            category: category.name,
        }).then(res => {
            dispatch(updateEvent({
                id: res.data.id,
                title: savedTitle,
                description,
                date,
                rating,
                icon,
                color,
                fontSize,
                width: place.width,
                height: place.height,
                pos_x: res.data.pos_x,
                pos_y: res.data.pos_y,
                category: category.name,
            }));
            if (rearranged(res).length) dispatch(updateEventsPositions(rearranged(res)));

            getCategoriesAPI().then(res => {
                dispatch(fetchedCategories(res.data));
            })
        }).catch((err) => {
            if (isDayFullError(err)) alert(NO_ROOM_MESSAGE);
            else console.log(err);
        })

        toggleForm(false);
    }

    const save = () => {
        if (!place || !canPlace(dayEvents, initialEvent, place)) {
            alert(initialEvent ? NO_ROOM_MESSAGE : noRoomMessage(dayEvents));
        } else if (eventId != null) {
            editEvent()
        } else {
            createEvent()
        }
    }

    const remove = (id) => {
        deleteEventAPI(id)
            .then(
                res => {
                    dispatch(removeEvent(id));
                    toggleForm(false);
                }
            )
            .catch(err => console.log("ERROR", err));
    };

    const handleCategoryEdit = (category) => {
        if (category.isEditable) {
            updateCategoryAPI(category.id, category.name).then(res => {
                if (res.status === 200)
                    dispatch(updateCategory(category));
            })
        }
    }

    const handleCategoryDelete = (category) => {
        deleteCategoryAPI(category.id).then(res => {
            if (res.status === 204)
                dispatch(deleteCategory(category.id));
            if (category.id === category.id) {
                setCategory({ id: null, name: "" })
            }
        });
    }

    const descriptionEditor = (
        <Suspense fallback={<div className="text-editor event-description" />}>
            <DescriptionEditor
                className="event-description"
                value={description}
                onChange={setDescription}
                placeholder="Добавьте описание"
                painter={painter}
                setPainter={setPainter}
            />
        </Suspense>
    );

    const heading = eventId != null ? "Редактирование" : "Новое событие";
    const dateField = <DateField value={day} onChange={setDay} />;
    const deleteControl = eventId != null && (
        <span className="form-delete" ref={deleteRef}>
            <button
                title="Удалить событие"
                className={classNames('btn', 'form-delete-btn', { 'form-delete-btn_active': confirmEventDeletion })}
                onClick={() => setConfirmEventDeletion(!confirmEventDeletion)}
            >
                <i className="icon-bin" />
            </button>
            {confirmEventDeletion && (
                <div className="form-delete-confirm">
                    <span>Удалить событие?</span>
                    <div className="form-delete-confirm-buttons">
                        <button className="btn form-delete-cancel" onClick={() => setConfirmEventDeletion(false)}>Отмена</button>
                        <button className="btn btn-red" onClick={() => remove(eventId)}>Удалить</button>
                    </div>
                </div>
            )}
        </span>
    );
    const saveButton = <button className="btn btn-green btn-save" onClick={() => save()}>Сохранить</button>;
    const colorPicker = <ColorPalette title="Цвет фона" value={color} onChange={setColor} />;
    const categorySelect = (
        <SearchBar
            options={categories.map(category => {
                return {
                    id: category.id,
                    name: category.name,
                }
            })}
            setValue={setCategory}
            handleOptionEdit={handleCategoryEdit}
            handleOptionDelete={handleCategoryDelete}
            value={category}
            classnames="category"
            placeholder={"Категория"}
        />
    );
    // The title, with the form's own controls under it, if any.
    const titleEditor = (own) => (
        <TextEditor
            className="event-title"
            value={title}
            onChange={setTitle}
            placeholder="Введите название"
            maxLength={200}
            multilineOnCtrlEnter
            defaultColor={DEFAULT_TEXT_COLOR}
            autoSize={titleFit}
            onAutoSize={setTitleFit}
            painter={painter}
            setPainter={setPainter}
        >
            {own}
        </TextEditor>
    );
    const layoutPicker = (
        <div className="event-layout-input">
            <EventLayout dayEvents={dayEvents} event={initialEvent} value={place} onChange={setPlace} />
        </div>
    );
    const descriptionField = descriptionApart ? (
        <div className="event-description-apart">
            <span>Описание открыто в отдельном окне</span>
            <button type="button" className="btn event-description-attach" onClick={() => setDescriptionApart(false)}>Вернуть в форму</button>
        </div>
    ) : (
        <div className="event-description-field">
            {descriptionEditor}
            <button type="button" className="event-description-detach" title="Открыть описание в отдельном окне" onClick={() => setDescriptionApart(true)}>
                {DETACH_ICON}
            </button>
        </div>
    );
    const descriptionWindow = descriptionApart && (
        <FloatingWindow
            name="description"
            defaultSize={descriptionWindowSize()}
            minSize={DESCRIPTION_WINDOW_MIN}
            cancel={WINDOW_CANCEL}
            onClose={() => setDescriptionApart(false)}
            closeTitle="Вернуть в форму"
        >
            <div className="description-window">
                <div className="description-window-header">
                    <span className="description-window-title">Описание</span>
                </div>
                {descriptionEditor}
            </div>
        </FloatingWindow>
    );

    if (wide) {
        return (
            <div className="new-day-event-form new-day-event-form_wide" ref={formRef}>
                <div className="event-form-main">
                    {titleEditor(null)}
                    {descriptionField}
                </div>
                <div className="event-form-side">
                    <div className="new-day-event-form-header">
                        <span className="new-day-event-form-title">{heading}</span>
                    </div>
                    <div className="event-form-side-actions">
                        {saveButton}
                        {deleteControl}
                    </div>
                    <div className="event-form-side-field">
                        <span className="event-form-side-label">Дата</span>
                        {dateField}
                    </div>
                    <div className="event-form-side-field event-form-side-field_row">
                        <span className="event-form-side-label">Цвет фона</span>
                        {colorPicker}
                    </div>
                    <div className="event-form-side-field">
                        <span className="event-form-side-label">Категория</span>
                        {categorySelect}
                    </div>
                    <div className="event-form-side-field">
                        <span className="event-form-side-label">Положение</span>
                        {layoutPicker}
                    </div>
                </div>
                {descriptionWindow}
            </div>
        );
    }

    return (
        <div className="new-day-event-form" ref={formRef}>
            <div className="new-day-event-form-header">
                <span className="new-day-event-form-title">{heading}</span>
                {dateField}
                {deleteControl}
                {saveButton}
            </div>
            {titleEditor(<>{colorPicker}{categorySelect}</>)}
            {layoutPicker}
            {descriptionField}
            {descriptionWindow}
        </div >
    )
}

export { EventForm };
